import { InstrumentStatus, RunStatus } from '../models/result.model';
import { ReportContentItem } from '../models/run.model';

/**
 * Evaluates the two run-quality thresholds that warrant warning the operator on study load and
 * after a calculation: instrument true-error rate, and the share of replicates discarded.
 *
 * Pure by design — no store, component or effect dependency — so the arithmetic is testable on its
 * own and the same predicate can be reused on the Instrument Errors page.
 */

/** Both metrics warn above 10%. Comparison is strictly greater than, so exactly 10.0% does not fire. */
export const DISCARD_WARNING_THRESHOLD = 0.1;

/** One instrument whose true-error rate breached the threshold. */
export interface InstrumentTrueErrorBreach {
  instrumentId: string;
  /**
   * Bare proportion in the range 0-1, exactly as the backend returns it. Scale by 100 for display.
   */
  percentTrueErrors: number;
}

/** One report content item whose discarded share breached the threshold. */
export interface DiscardScopeBreach {
  reportContentItemId: string;
  kitLot: string;
  /** From the report content item's runId, which carries the instrument ID. */
  instrumentId: string;
  beadRegion: string;
  discardedCount: number;
  analyzedCount: number;
  /** Bare proportion in the range 0-1. */
  discardedFraction: number;
}

/** What the warning dialog renders. */
export interface DiscardWarningSummary {
  totalDiscarded: number;
  totalAnalyzed: number;
  /** Bare proportion in the range 0-1; zero when nothing was analyzed. */
  totalDiscardedFraction: number;
  discardBreaches: DiscardScopeBreach[];
  instrumentBreaches: InstrumentTrueErrorBreach[];
}

/** True when this instrument's true-error rate warrants a warning. */
export function isInstrumentTrueErrorBreach(instrument: InstrumentStatus | null | undefined): boolean {
  const rate = instrument?.percentTrueErrors;
  return typeof rate === 'number' && Number.isFinite(rate) && rate > DISCARD_WARNING_THRESHOLD;
}

/**
 * Total replicates discarded for one report content item: the four discard reasons reported on the
 * Assay Analysis page. Outliers are included, so this total can exceed the number of replicates that
 * carry a discard reason.
 */
export function sumDiscardedReplicates(runStatus: RunStatus | null | undefined): number {
  if (!runStatus) {
    return 0;
  }
  return toCount(runStatus.numRVsDiscardedAssayAndRvErrors)
    + toCount(runStatus.numRVsDiscardedBeadCountErrors)
    + toCount(runStatus.numRVsDiscardedCalFailed)
    + toCount(runStatus.numRVsDiscardedOutliers);
}

/**
 * Builds the warning summary for a study.
 *
 * @param instrumentStatus Per-instrument error rates from the calculation response.
 * @param runStatuses Per-report-content-item run status from the same response.
 * @param reportContentItems Used only to label a breaching scope by kit lot, run and bead region.
 * @returns The summary when either threshold is breached, otherwise null.
 */
export function evaluateDiscardThresholds(
  instrumentStatus: InstrumentStatus[] | null | undefined,
  runStatuses: RunStatus[] | null | undefined,
  reportContentItems: ReportContentItem[] | null | undefined
): DiscardWarningSummary | null {
  const reportContentById = new Map<string, ReportContentItem>(
    (reportContentItems ?? [])
      .filter(item => !!item?.reportContentItemId)
      .map(item => [item.reportContentItemId, item])
  );

  let totalDiscarded = 0;
  let totalAnalyzed = 0;
  const discardBreaches: DiscardScopeBreach[] = [];

  (runStatuses ?? []).forEach(runStatus => {
    const discardedCount = sumDiscardedReplicates(runStatus);
    const analyzedCount = toCount(runStatus?.numRVsAnalyzed);
    totalDiscarded += discardedCount;
    totalAnalyzed += analyzedCount;

    // A scope that analyzed nothing has no meaningful rate, and must never divide by zero.
    if (analyzedCount <= 0) {
      return;
    }
    const discardedFraction = discardedCount / analyzedCount;
    if (discardedFraction <= DISCARD_WARNING_THRESHOLD) {
      return;
    }
    const reportContentItem = reportContentById.get(runStatus.reportContentItemId);
    discardBreaches.push({
      reportContentItemId: runStatus.reportContentItemId,
      kitLot: reportContentItem?.lot ?? '-',
      instrumentId: reportContentItem?.runId ?? '-',
      beadRegion: reportContentItem?.region !== undefined && reportContentItem?.region !== null
        ? String(reportContentItem.region)
        : '-',
      discardedCount,
      analyzedCount,
      discardedFraction
    });
  });

  const instrumentBreaches: InstrumentTrueErrorBreach[] = (instrumentStatus ?? [])
    .filter(instrument => isInstrumentTrueErrorBreach(instrument))
    .map(instrument => ({
      instrumentId: instrument.instrumentId,
      percentTrueErrors: instrument.percentTrueErrors
    }));

  if (!discardBreaches.length && !instrumentBreaches.length) {
    return null;
  }

  return {
    totalDiscarded,
    totalAnalyzed,
    totalDiscardedFraction: totalAnalyzed > 0 ? totalDiscarded / totalAnalyzed : 0,
    discardBreaches,
    instrumentBreaches
  };
}

function toCount(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
